import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Industry } from '@core/models/user.model';
import { EnvironmentService } from '@core/services/environment.service';
import { buildUrl } from '@core/utils/url.utilities';
import { ResponseOrganizationCacheDto } from '@kerberos-compliance/compliance-shared-ts/features/organization/dtos/response.organization.dtos';
import { firstValueFrom } from 'rxjs';

type IndustryNameResponse = {
  name: string;
};

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private readonly httpClient = inject(HttpClient);

  private readonly environmentService = inject(EnvironmentService);

  /**
   * Fetches organization details by ID (via risk-analysis endpoint).
   *
   * @param {string} organizationId - The unique identifier of the organization.
   * @returns {Promise<ResponseOrganizationCacheDto>} A promise that resolves to the organization details.
   */
  public async getOrganizationDetails(organizationId: string): Promise<ResponseOrganizationCacheDto> {
    return firstValueFrom(
      this.httpClient.get<ResponseOrganizationCacheDto>(
        buildUrl(this.environmentService.baseAdpHost, `risk-analysis/organization/${organizationId}`),
        { withCredentials: true },
      ),
    );
  }

  /**
   * Fetches industry name by ID (via risk-analysis endpoint).
   *
   * @param {number} industryId - The numeric ID of the industry.
   * @returns {Promise<Industry | undefined>} A promise that resolves to the Industry enum value.
   */
  public async getIndustryName(industryId: number): Promise<Industry | undefined> {
    const response = await firstValueFrom(
      this.httpClient.get<IndustryNameResponse>(
        buildUrl(this.environmentService.baseAdpHost, `risk-analysis/industry/${industryId}`),
        { withCredentials: true },
      ),
    );

    return response.name && Object.values(Industry).includes(response.name as Industry)
      ? (response.name as Industry)
      : undefined;
  }
}
